// Fix: Corrected ScheduleStatus import from type to value (27.07.2025)
// Fix: Fixed Priority enum handling and i18next translations (27.07.2025)
// Fix: Updated MUI Grid to v2 syntax - removing item prop and using size (27.07.2025)
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  IconButton,
  Paper,
  Collapse,
  Grid,
  Typography,
  Switch,
  FormControlLabel,
  InputAdornment,
  Autocomplete,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Search,
  FilterList,
  Clear,
  ExpandMore,
  ExpandLess,
  Schedule as ScheduleIcon,
  PlayArrow,
  Stop,
  Warning,
  CheckCircle,
  Edit,
  CalendarToday,
  AccessTime,
  Person,
  PlaylistPlay,
  Devices,
  Repeat,
} from '@mui/icons-material';

import { ScheduleStatus, Priority } from "../../types/index";
import type { ScheduleStats, SearchFilters, SortOptions } from "../../types/index";

interface ScheduleFiltersProps {
  filters: SearchFilters;
  sortOptions: SortOptions;
  onFiltersChange: (filters: SearchFilters) => void;
  onSortChange: (sort: SortOptions) => void;
  onClearFilters: () => void;
  totalItems?: number;
  scheduleStats?: ScheduleStats;
  availableAuthors?: { id: number; name: string }[];
  availablePlaylists?: { id: number; name: string }[];
  availableDevices?: { id: number; name: string }[];
  viewMode?: 'list' | 'calendar';
  onViewModeChange?: (mode: 'list' | 'calendar') => void;
}

const ScheduleFilters: React.FC<ScheduleFiltersProps> = ({
  filters,
  sortOptions,
  onFiltersChange,
  onSortChange,
  onClearFilters,
  totalItems = 0,
  scheduleStats,
  availableAuthors = [],
  availablePlaylists = [],
  availableDevices = [],
  viewMode = 'list',
  onViewModeChange,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      search: event.target.value,
    });
  };

  const handleStatusChange = (event: any) => {
    const value = event.target.value;
    onFiltersChange({
      ...filters,
      scheduleStatuses: typeof value === 'string' ? value.split(',') : value,
    });
  };

  const handlePriorityChange = (event: any) => {
    const value = event.target.value;
    onFiltersChange({
      ...filters,
      priorities: typeof value === 'string' ? value.split(',') : value,
    });
  };

  const handleAuthorChange = (event: any, newValue: any) => {
    onFiltersChange({
      ...filters,
      createdBy: newValue?.id || undefined,
    });
  };

  const handlePlaylistChange = (event: any, newValue: any) => {
    onFiltersChange({
      ...filters,
      playlistId: newValue?.id || undefined,
    });
  };

  const handleDeviceChange = (event: any, newValue: any) => {
    onFiltersChange({
      ...filters,
      deviceId: newValue?.id || undefined,
    });
  };

  const handleDateFromChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      dateFrom: event.target.value,
    });
  };

  const handleDateToChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      dateTo: event.target.value,
    });
  };

  const handleTimeFromChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      timeFrom: event.target.value,
    });
  };

  const handleTimeToChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      timeTo: event.target.value,
    });
  };

  const handleActiveOnlyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      activeOnly: event.target.checked,
    });
  };

  const handleRecurringOnlyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      recurringOnly: event.target.checked,
    });
  };

  const handleSortFieldChange = (event: any) => {
    onSortChange({
      ...sortOptions,
      field: event.target.value,
    });
  };

  const handleSortDirectionChange = (event: any) => {
    onSortChange({
      ...sortOptions,
      direction: event.target.value,
    });
  };

  const getActiveFiltersCount = (): number => {
    let count = 0;
    if (filters.search) count++;
    if (filters.scheduleStatuses && filters.scheduleStatuses.length > 0) count++;
    if (filters.priorities && filters.priorities.length > 0) count++;
    if (filters.createdBy) count++;
    if (filters.playlistId) count++;
    if (filters.deviceId) count++;
    if (filters.dateFrom) count++;
    if (filters.dateTo) count++;
    if (filters.timeFrom) count++;
    if (filters.timeTo) count++;
    if (filters.activeOnly) count++;
    if (filters.recurringOnly) count++;
    return count;
  };

  const getStatusIcon = (status: ScheduleStatus) => {
    switch (status) {
      case ScheduleStatus.ACTIVE:
        return <PlayArrow fontSize="small" />;
      case ScheduleStatus.SCHEDULED:
        return <ScheduleIcon fontSize="small" />;
      case ScheduleStatus.DRAFT:
        return <Edit fontSize="small" />;
      case ScheduleStatus.COMPLETED:
        return <CheckCircle fontSize="small" />;
      case ScheduleStatus.CANCELLED:
        return <Stop fontSize="small" />;
      case ScheduleStatus.CONFLICT:
        return <Warning fontSize="small" />;
      default:
        return <ScheduleIcon fontSize="small" />;
    }
  };

  const getPriorityKey = (priority: Priority): string => {
    // Безопасная проверка на случай undefined или неправильного типа
    if (priority === undefined || priority === null) {
      console.warn('getPriorityKey: received undefined/null priority, using normal');
      return 'normal';
    }

    switch (Number(priority)) {
      case Priority.LOW: // 1
        return 'low';
      case Priority.NORMAL: // 5
        return 'normal';
      case Priority.HIGH: // 10
        return 'high';
      case Priority.URGENT: // 15
        return 'urgent';
      case Priority.CRITICAL: // 20
        return 'critical';
      default:
        console.warn(`getPriorityKey: unknown priority value ${priority}, using normal`);
        return 'normal';
    }
  };

  const getPriorityIcon = (priority: Priority) => {
    switch (priority) {
      case Priority.CRITICAL:
        return '🔴';
      case Priority.URGENT:
        return '🟠';
      case Priority.HIGH:
        return '🟡';
      case Priority.NORMAL:
        return '🟢';
      case Priority.LOW:
        return '⚪';
      default:
        return '⚪';
    }
  };

  const activeFiltersCount = getActiveFiltersCount();

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
      {/* Основная строка фильтров */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: expanded ? 2 : 0 }}>
        {/* Поиск */}
        <TextField
          placeholder={t('schedule.searchPlaceholder')}
          value={filters.search || ''}
          onChange={handleSearchChange}
          variant="outlined"
          size="small"
          sx={{ flexGrow: 1, maxWidth: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: filters.search && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => onFiltersChange({ ...filters, search: '' })}
                >
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Статус */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{t('common.status')}</InputLabel>
          <Select
            multiple
            value={filters.scheduleStatuses || []}
            onChange={handleStatusChange}
            label={t('common.status')}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected as string[]).map((value) => (
                  <Chip
                    key={value}
                    label={t(`schedule.status.${value.toLowerCase()}`)}
                    size="small"
                    icon={getStatusIcon(value as ScheduleStatus)}
                    color={
                      value === ScheduleStatus.ACTIVE ? 'success' :
                      value === ScheduleStatus.SCHEDULED ? 'info' :
                      value === ScheduleStatus.CONFLICT ? 'error' :
                      value === ScheduleStatus.DRAFT ? 'warning' : 'default'
                    }
                  />
                ))}
              </Box>
            )}
          >
            {Object.values(ScheduleStatus).map((status) => (
              <MenuItem key={status} value={status}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getStatusIcon(status)}
                  {t(`schedule.status.${status.toLowerCase()}`)}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Приоритет */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>{t('schedule.priorityLabel')}</InputLabel>
          <Select
            multiple
            value={filters.priorities || []}
            onChange={handlePriorityChange}
            label={t('schedule.priorityLabel')}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected as Priority[]).map((value) => (
                  <Chip
                    key={value}
                    label={t(`schedule.priorities.${getPriorityKey(value)}`)}
                    size="small"
                    icon={<span>{getPriorityIcon(value)}</span>}
                    color={
                      value === Priority.URGENT ? 'error' :
                      value === Priority.HIGH ? 'warning' :
                      value === Priority.NORMAL ? 'info' : 'default'
                    }
                  />
                ))}
              </Box>
            )}
          >
            {Object.values(Priority).filter(p => typeof p === 'number').map((priority) => (
              <MenuItem key={priority} value={priority}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{getPriorityIcon(priority as Priority)}</span>
                  {t(`schedule.priorities.${getPriorityKey(priority as Priority)}`)}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Режим отображения */}
        {onViewModeChange && (
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newMode) => newMode && onViewModeChange(newMode)}
            size="small"
          >
            <ToggleButton value="list" aria-label="list view">
              <ScheduleIcon />
            </ToggleButton>
            <ToggleButton value="calendar" aria-label="calendar view">
              <CalendarToday />
            </ToggleButton>
          </ToggleButtonGroup>
        )}

        {/* Сортировка */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>{t('common.sortBy')}</InputLabel>
          <Select
            value={sortOptions.field || 'start_time'}
            onChange={handleSortFieldChange}
            label={t('common.sortBy')}
          >
            <MenuItem value="name">{t('schedule.name')}</MenuItem>
            <MenuItem value="start_time">{t('schedule.startTime')}</MenuItem>
            <MenuItem value="end_time">{t('schedule.endTime')}</MenuItem>
            <MenuItem value="status">{t('common.status')}</MenuItem>
            <MenuItem value="priority">{t('schedule.priorityLabel')}</MenuItem>
            <MenuItem value="created_at">{t('common.dateCreated')}</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>{t('common.order')}</InputLabel>
          <Select
            value={sortOptions.direction || 'asc'}
            onChange={handleSortDirectionChange}
            label={t('common.order')}
          >
            <MenuItem value="asc">{t('common.ascending')}</MenuItem>
            <MenuItem value="desc">{t('common.descending')}</MenuItem>
          </Select>
        </FormControl>

        {/* Кнопки управления */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FilterList />}
            endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
            onClick={() => setExpanded(!expanded)}
            color={activeFiltersCount > 0 ? 'primary' : 'inherit'}
          >
            {t('common.filters')}
            {activeFiltersCount > 0 && (
              <Chip
                label={activeFiltersCount}
                size="small"
                color="primary"
                sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Button>

          {activeFiltersCount > 0 && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<Clear />}
              onClick={onClearFilters}
              color="error"
            >
              {t('common.clear')}
            </Button>
          )}
        </Box>
      </Box>

      {/* Расширенные фильтры */}
      <Collapse in={expanded}>
        <Grid container spacing={2}>
          {/* Первая строка - Селекторы */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Autocomplete
              options={availableAuthors}
              getOptionLabel={(option) => option.name}
              value={availableAuthors.find(author => author.id === filters.createdBy) || null}
              onChange={handleAuthorChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('schedule.author')}
                  placeholder={t('schedule.selectAuthor')}
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Autocomplete
              options={availablePlaylists}
              getOptionLabel={(option) => option.name}
              value={availablePlaylists.find(playlist => playlist.id === filters.playlistId) || null}
              onChange={handlePlaylistChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('schedule.playlist')}
                  placeholder={t('schedule.selectPlaylist')}
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <PlaylistPlay fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Autocomplete
              options={availableDevices}
              getOptionLabel={(option) => option.name}
              value={availableDevices.find(device => device.id === filters.deviceId) || null}
              onChange={handleDeviceChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('schedule.device')}
                  placeholder={t('schedule.selectDevice')}
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Devices fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {/* Вторая строка - Даты */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('schedule.dateRange')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label={t('common.dateFrom')}
                type="date"
                size="small"
                value={filters.dateFrom || ''}
                onChange={handleDateFromChange}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ flexGrow: 1 }}
              />
              <TextField
                label={t('common.dateTo')}
                type="date"
                size="small"
                value={filters.dateTo || ''}
                onChange={handleDateToChange}
                InputLabelProps={{ shrink: true }}
                sx={{ flexGrow: 1 }}
              />
            </Box>
          </Grid>

          {/* Третья строка - Время */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('schedule.timeRange')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label={t('schedule.timeFrom')}
                type="time"
                size="small"
                value={filters.timeFrom || ''}
                onChange={handleTimeFromChange}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccessTime fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ flexGrow: 1 }}
              />
              <TextField
                label={t('schedule.timeTo')}
                type="time"
                size="small"
                value={filters.timeTo || ''}
                onChange={handleTimeToChange}
                InputLabelProps={{ shrink: true }}
                sx={{ flexGrow: 1 }}
              />
            </Box>
          </Grid>

          {/* Быстрые фильтры */}
          <Grid size={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" gutterBottom>
              {t('schedule.quickFilters')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={filters.activeOnly || false}
                    onChange={handleActiveOnlyChange}
                    size="small"
                  />
                }
                label={t('schedule.activeOnly')}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={filters.recurringOnly || false}
                    onChange={handleRecurringOnlyChange}
                    size="small"
                  />
                }
                label={t('schedule.recurringOnly')}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={filters.scheduleStatuses?.includes(ScheduleStatus.CONFLICT) || false}
                    onChange={(e) => {
                      const conflictStatuses = e.target.checked
                        ? [ScheduleStatus.CONFLICT]
                        : [];
                      onFiltersChange({
                        ...filters,
                        scheduleStatuses: conflictStatuses,
                      });
                    }}
                    size="small"
                  />
                }
                label={t('schedule.conflictsOnly')}
              />
            </Box>
          </Grid>

          {/* Статистика */}
          <Grid size={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" gutterBottom>
              {t('schedule.statistics')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary">
                {t('schedule.totalSchedules')}: {totalItems}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('schedule.activeSchedules')}: {scheduleStats?.active_schedules || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('schedule.scheduledSchedules')}: {scheduleStats?.scheduled_events || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('schedule.completedSchedules')}: {scheduleStats?.completed_events || 0}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Информация о результатах */}
        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            {t('common.showingResults', { total: totalItems })}
          </Typography>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default ScheduleFilters;



